// Focal-point framing: x/y (0–100) place a point of the image at the same
// percentage point of its container; zoom (1–4) magnifies around that point.
// With object-fit: cover this renders consistently at any container aspect,
// which is why we store a focal point instead of a crop rectangle.

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const DEFAULT_FRAMING = { x: 50, y: 50, zoom: 1 };

function clamp(value, min, max, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

export function normalizeFraming(framing) {
  return {
    x: clamp(framing?.x, 0, 100, DEFAULT_FRAMING.x),
    y: clamp(framing?.y, 0, 100, DEFAULT_FRAMING.y),
    zoom: clamp(framing?.zoom, ZOOM_MIN, ZOOM_MAX, DEFAULT_FRAMING.zoom),
  };
}

// Normalize all three per-item contexts (used to hydrate the admin form)
export function normalizeAllFraming(framing) {
  return {
    tile: normalizeFraming(framing?.tile),
    detail: normalizeFraming(framing?.detail),
    carousel: normalizeFraming(framing?.carousel),
  };
}

// Detail-modal framing for secondary images, keyed by URL (the primary
// image's detail framing lives in framing.detail). Drops entries without
// a URL and clamps the rest (used to hydrate the admin form).
export function normalizeImageFramings(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((entry) => typeof entry?.url === "string" && entry.url)
    .map((entry) => ({ url: entry.url, ...normalizeFraming(entry) }));
}

// Inline style for an <img> inside a positioned overflow-hidden container.
// The zoom is expressed as geometry (oversized box pulled back by the focal
// offsets) rather than transform: scale() — Safari rasterizes composited
// layers at their untransformed size, so a scale()-zoomed image goes blurry
// whenever any animation (e.g. tile hover) promotes it to a GPU layer.
// Equivalent crop: cover-fitting a zoom×-sized box multiplies the painted
// image by zoom, and left/top place the box's focal point (which
// object-position pins to x%/y% of the painted image) at x%/y% of the
// container — the same fixed point scale() kept via transform-origin.
export function framingStyle(framing) {
  const { x, y, zoom } = normalizeFraming(framing);
  return {
    position: "absolute",
    width: `${zoom * 100}%`,
    height: `${zoom * 100}%`,
    maxWidth: "none", // preflight's img { max-width: 100% } would clamp the zoom

    left: `${x * (1 - zoom)}%`,
    top: `${y * (1 - zoom)}%`,
    objectFit: "cover",
    objectPosition: `${x}% ${y}%`,
  };
}

// Aspect ratio of the detail-modal image window per kind; the per-item
// framing decides how the scan sits in it. Covers get a shorter window:
// 8:5 renders 816×510 at the modal's full inner width.
export function detailAspect(kind) {
  return kind === "cover" ? "8 / 5" : "3 / 2";
}
